import React, { useState, useEffect, useCallback } from 'react';
import { ConsentChecks, consentsValid } from '../components/ConsentChecks';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Video, Mail, Lock, User, Building, Loader2, AlertCircle, Globe, Check, X, Shield } from 'lucide-react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Password strength checker
function checkPasswordStrength(password) {
  const checks = {
    length: password.length >= 10,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password)
  };
  
  const passedChecks = Object.values(checks).filter(Boolean).length;
  let strength = 'weak';
  if (passedChecks >= 5) strength = 'strong';
  else if (passedChecks >= 3) strength = 'medium';
  
  return { checks, strength, passedChecks };
}

// Password strength indicator component
function PasswordStrengthIndicator({ password }) {
  const { checks, strength, passedChecks } = checkPasswordStrength(password);
  
  if (!password) return null;
  
  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500'
  };
  
  const strengthLabels = {
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong'
  };

  return (
    <div className="mt-3 space-y-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div 
            className={`h-full ${strengthColors[strength]} transition-all duration-300`}
            style={{ width: `${(passedChecks / 5) * 100}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${
          strength === 'strong' ? 'text-green-400' : 
          strength === 'medium' ? 'text-yellow-400' : 'text-red-400'
        }`}>
          {strengthLabels[strength]}
        </span>
      </div>
      
      {/* Requirements checklist */}
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div className={`flex items-center gap-1 ${checks.length ? 'text-green-400' : 'text-zinc-500'}`}>
          {checks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>10+ characters</span>
        </div>
        <div className={`flex items-center gap-1 ${checks.uppercase ? 'text-green-400' : 'text-zinc-500'}`}>
          {checks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>Uppercase letter</span>
        </div>
        <div className={`flex items-center gap-1 ${checks.lowercase ? 'text-green-400' : 'text-zinc-500'}`}>
          {checks.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>Lowercase letter</span>
        </div>
        <div className={`flex items-center gap-1 ${checks.number ? 'text-green-400' : 'text-zinc-500'}`}>
          {checks.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>Number</span>
        </div>
        <div className={`flex items-center gap-1 ${checks.special ? 'text-green-400' : 'text-zinc-500'}`}>
          {checks.special ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>Special character</span>
        </div>
      </div>
    </div>
  );
}

// Inner login component that uses reCAPTCHA hook
function LoginPageInner() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attemptsWarning, setAttemptsWarning] = useState('');

  const from = location.state?.from || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAttemptsWarning('');
    setLoading(true);

    try {
      // Get reCAPTCHA token if available
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('login');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA failed, proceeding without:', recaptchaError);
        }
      }
      
      await login(email, password, recaptchaToken);
      navigate(from);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid email or password';
      setError(formatApiError(detail));
      
      // Check if it's a rate limit warning
      if (err.response?.status === 429) {
        setAttemptsWarning('');
      } else if (detail.includes('attempts remaining')) {
        setAttemptsWarning(detail);
        setError('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <img src={LOGO_URL} alt="BrandStage" className="h-12 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold mt-4">Welcome back</h1>
          <p className="text-zinc-500">Sign in to your account</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {error && (
            <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-sm flex items-center gap-2" data-testid="login-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}
          
          {attemptsWarning && (
            <div className="bg-yellow-900/20 border border-yellow-700 text-yellow-400 px-4 py-3 rounded-sm flex items-center gap-2">
              <Shield className="w-5 h-5 flex-shrink-0" />
              {attemptsWarning}
            </div>
          )}

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-dark rounded-sm w-full pl-10"
                placeholder="you@company.com"
                data-testid="login-email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-dark rounded-sm w-full pl-10"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary rounded-sm w-full flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
          
          <div className="text-center mt-4">
            <Link 
              to="/forgot-password" 
              className="text-sm text-zinc-500 hover:text-[#C41E3A] transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </Link>
          </div>
        </form>

        <p className="text-center mt-6 text-zinc-500">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="text-[#C41E3A] hover:underline" data-testid="register-link">
            Create one
          </Link>
        </p>
        
        {/* Security notice */}
        <p className="text-center mt-4 text-xs text-zinc-600">
          Protected by reCAPTCHA
        </p>
      </div>
    </div>
  );
}

export function LoginPage() {
  // Wrap with reCAPTCHA provider only if site key is configured
  if (RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
        <LoginPageInner />
      </GoogleReCaptchaProvider>
    );
  }
  return <LoginPageInner />;
}

// Inner register component that uses reCAPTCHA hook
function RegisterPageInner() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: '',
    country: 'india',
    terms_accepted: false,
    data_processing_accepted: false,
    marketing_consent: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Get redirect URL if coming from claim-profile
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  
  // Password strength check
  const { checks, strength } = checkPasswordStrength(formData.password);
  const isPasswordValid = Object.values(checks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!consentsValid(formData)) {
      setError('Please accept the Terms, Privacy Policy and data processing consent to continue');
      return;
    }
    
    // Validate password strength
    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }
    
    setLoading(true);

    try {
      // Get reCAPTCHA token if available
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('register');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA failed, proceeding without:', recaptchaError);
        }
      }
      
      await register({ ...formData, recaptcha_token: recaptchaToken, ref_code: localStorage.getItem('brandstage_ref') || '' });
      localStorage.removeItem('brandstage_ref');  // Clear after use
      navigate(redirectUrl);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center" data-testid="register-page">
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <img src={LOGO_URL} alt="BrandStage" className="h-12 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold mt-4">Create your account</h1>
          <p className="text-zinc-500">Build your profile and share your story</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          {error && (
            <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-sm flex items-center gap-2" data-testid="register-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Full Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="input-dark rounded-sm w-full pl-10"
                placeholder="John Doe"
                data-testid="register-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="input-dark rounded-sm w-full pl-10"
                placeholder="you@company.com"
                data-testid="register-email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Password *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="input-dark rounded-sm w-full pl-10"
                placeholder="Create a strong password"
                data-testid="register-password"
              />
            </div>
            <PasswordStrengthIndicator password={formData.password} />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Company (optional)</label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="input-dark rounded-sm w-full pl-10"
                placeholder="Your Company"
                data-testid="register-company"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Country *</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <select
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                required
                className="input-dark rounded-sm w-full pl-10 appearance-none cursor-pointer"
                data-testid="register-country"
              >
                <option value="india">India</option>
                <option value="international">Outside India</option>
              </select>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              {formData.country === 'india' 
                ? 'Payments will be in INR with applicable GST' 
                : 'Payments will be in USD'}
            </p>
          </div>
          
          <ConsentChecks value={formData} onChange={(c) => setFormData(c)} prefix="register" />

          <button
            type="submit"
            disabled={loading || !consentsValid(formData) || !isPasswordValid}
            className={`w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors ${
              consentsValid(formData) && isPasswordValid
                ? 'bg-[#C41E3A] hover:bg-[#A01830] text-white' 
                : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
            }`}
            data-testid="register-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-zinc-500">
          Already have an account?{' '}
          <Link to="/login" className="text-[#C41E3A] hover:underline" data-testid="login-link">
            Sign in
          </Link>
        </p>
        
        {/* Security notice */}
        <p className="text-center mt-4 text-xs text-zinc-600">
          Protected by reCAPTCHA
        </p>
      </div>
    </div>
  );
}

export function RegisterPage() {
  // Wrap with reCAPTCHA provider only if site key is configured
  if (RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
        <RegisterPageInner />
      </GoogleReCaptchaProvider>
    );
  }
  return <RegisterPageInner />;
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Something went wrong');
      }
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="w-full max-w-md px-6 text-center">
          <div className="mb-8">
            <Link to="/" className="inline-block mb-4">
              <img src={LOGO_URL} alt="BrandStage" className="h-12 w-auto mx-auto" />
            </Link>
          </div>
          
          <div className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-lg">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold mb-2">Check Your Email</h2>
            <p className="text-zinc-400 mb-6">
              If an account exists with <span className="text-white">{email}</span>, you&apos;ll receive a password reset link shortly.
            </p>
            <Link 
              to="/login" 
              className="text-[#C41E3A] hover:underline"
            >
              ← Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <img src={LOGO_URL} alt="BrandStage" className="h-12 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold">Reset Password</h1>
          <p className="text-zinc-400 mt-2">Enter your email to receive a reset link</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-zinc-400 text-sm mb-2">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg pl-12 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A] transition-colors"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary rounded-sm w-full flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-zinc-500">
          Remember your password?{' '}
          <Link to="/login" className="text-[#C41E3A] hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Get token and email from URL
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const email = urlParams.get('email');
  
  // Password strength check
  const { checks, strength } = checkPasswordStrength(password);
  const isPasswordValid = Object.values(checks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, new_password: password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="w-full max-w-md px-6 text-center">
          <div className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-lg">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Invalid Reset Link</h2>
            <p className="text-zinc-400 mb-6">
              This password reset link is invalid or has expired.
            </p>
            <Link 
              to="/forgot-password" 
              className="text-[#C41E3A] hover:underline"
            >
              Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="w-full max-w-md px-6 text-center">
          <div className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-lg">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold mb-2">Password Reset!</h2>
            <p className="text-zinc-400 mb-6">
              Your password has been successfully reset. Redirecting to login...
            </p>
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#C41E3A]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <img src={LOGO_URL} alt="BrandStage" className="h-12 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold">Create New Password</h1>
          <p className="text-zinc-400 mt-2">Enter your new password below</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-zinc-400 text-sm mb-2">New Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Create a strong password"
                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg pl-12 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A] transition-colors"
              />
            </div>
            <PasswordStrengthIndicator password={password} />
          </div>

          <div>
            <label className="block text-zinc-400 text-sm mb-2">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg pl-12 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A] transition-colors"
              />
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                <X className="w-3 h-3" /> Passwords do not match
              </p>
            )}
            {confirmPassword && password === confirmPassword && (
              <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> Passwords match
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !isPasswordValid || password !== confirmPassword}
            className={`w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors ${
              isPasswordValid && password === confirmPassword
                ? 'bg-[#C41E3A] hover:bg-[#A01830] text-white'
                : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
